import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, MessageSquare, Eye } from 'lucide-react';
import RequestQuoteModal from './RequestQuoteModal';
import PackageDetailsModal from './PackageDetailsModal';

/**
 * Renders the action area for a partner package card:
 *   1. A subtle "Details" button → opens PackageDetailsModal with the full
 *      description + media carousel + a CTA inside.
 *   2. The primary CTA based on booking_type:
 *        - iframe         → "View Availability" → /package-viewer
 *        - external_link  → "Book Now" → supplier_url
 *        - quote_request  → "Request Quote" → opens RequestQuoteModal
 */
const PackageCTA = ({ pkg, size = 'md', className = '', testIdPrefix = 'pkg-cta' }) => {
  const { t } = useTranslation();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const type = pkg?.booking_type || 'iframe';

  // Deep-link support: when arriving with ?package=<this pkg id> (e.g. from a
  // shared OG link), auto-open the details modal for this card and scroll it
  // into view. Strip the query param afterwards so the URL is clean.
  useEffect(() => {
    if (!pkg?.id) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('package');
    if (targetId !== pkg.id) return;
    setDetailsOpen(true);
    // Find the card wrapper this CTA lives in and scroll it into view.
    setTimeout(() => {
      const node = document.querySelector(`[data-testid$="-${pkg.id}"]`);
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Clean the URL so back-button / refresh don't keep re-opening it.
      window.history.replaceState({}, '', window.location.pathname);
    }, 200);
  }, [pkg?.id]);

  const sizeCls = size === 'sm' ? 'px-3 py-2 text-[11px]' : 'px-3.5 py-2 text-xs';
  const baseCls = `inline-flex items-center gap-1.5 bg-terracotta text-white hover:bg-terracotta/90 font-bold uppercase tracking-wide rounded-sm transition-all hover:translate-y-[-1px] ${sizeCls} ${className}`;
  const detailsCls = `inline-flex items-center gap-1.5 border border-maroon/25 text-maroon hover:bg-maroon/5 font-semibold uppercase tracking-wide rounded-sm transition-colors ${sizeCls}`;

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          className={detailsCls}
          data-testid={`${testIdPrefix}-details-${pkg.id}`}
        >
          <Eye size={12} />
          {t('packages.details', 'Details')}
        </button>

        {type === 'quote_request' && (
          <button
            type="button"
            onClick={() => setQuoteOpen(true)}
            className={baseCls}
            data-testid={`${testIdPrefix}-quote-${pkg.id}`}
          >
            <MessageSquare size={12} />
            {t('quote.cta')}
          </button>
        )}

        {type === 'external_link' && (
          <a
            href={pkg.supplier_url}
            target="_blank"
            rel="noopener noreferrer"
            className={baseCls}
            data-testid={`${testIdPrefix}-extlink-${pkg.id}`}
          >
            {t('bookings.bookNow')}
            <ExternalLink size={12} />
          </a>
        )}

        {type !== 'quote_request' && type !== 'external_link' && (
          <a
            href={`/package-viewer?destination=${encodeURIComponent(pkg.supplier_url || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className={baseCls}
            data-testid={`${testIdPrefix}-iframe-${pkg.id}`}
          >
            {t('bookings.viewAvailability')}
            <ExternalLink size={12} />
          </a>
        )}
      </div>

      <PackageDetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} pkg={pkg} />
      <RequestQuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} pkg={pkg} />
    </>
  );
};

export default PackageCTA;
